"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Fait apparaître son contenu à l'entrée dans le viewport.
 * `prefers-reduced-motion` est traité en CSS (`motion-reduce:*`) plutôt qu'en
 * JS : le contenu reste visible même si l'observer ne se déclenche jamais.
 */
function Reveal({ className, children, ...props }: React.ComponentProps<"div">) {
  const ref = React.useRef<HTMLDivElement>(null)
  const [visible, setVisible] = React.useState(false)

  React.useEffect(() => {
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setVisible(true)
        observer.disconnect()
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    )

    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      className={cn(
        "transition-[opacity,transform] duration-700 ease-out",
        visible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0",
        "motion-reduce:translate-y-0 motion-reduce:opacity-100 motion-reduce:transition-none",
        className
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export { Reveal }
